import { useEffect, useMemo, useRef } from 'react';

type ThrottledFn<Args extends unknown[]> = ((...args: Args) => void) & {
  cancel: () => void;
  flush: () => void;
};

/**
 * Returns a stable throttled wrapper around `fn`. Invocations are rate-limited
 * to fire at most once every `ms` milliseconds, with leading- and trailing-edge
 * calls: the first call fires synchronously, subsequent calls within the
 * window are coalesced into a single trailing call with the latest args.
 *
 * Sibling to `useDebounceCallback` — use this when the first call should land
 * immediately and rapid follow-ups should be coalesced rather than each
 * resetting the timer.
 */
export const useThrottledCallback = <Args extends unknown[]>(
  fn: (...args: Args) => void,
  ms: number
): ThrottledFn<Args> => {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastInvokeTimeRef = useRef(0);
  const lastArgsRef = useRef<Args | null>(null);

  const throttled = useMemo(() => {
    const cancel = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      lastArgsRef.current = null;
    };

    const flush = () => {
      if (timerRef.current && lastArgsRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        const args = lastArgsRef.current;
        lastArgsRef.current = null;
        lastInvokeTimeRef.current = Date.now();
        fnRef.current(...args);
      }
    };

    const invoke = (...args: Args) => {
      const now = Date.now();
      const elapsed = now - lastInvokeTimeRef.current;

      if (elapsed >= ms) {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        lastArgsRef.current = null;
        lastInvokeTimeRef.current = now;
        fnRef.current(...args);
        return;
      }

      lastArgsRef.current = args;
      if (timerRef.current) return;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        const argsToFire = lastArgsRef.current;
        lastArgsRef.current = null;
        lastInvokeTimeRef.current = Date.now();
        if (argsToFire) fnRef.current(...argsToFire);
      }, ms - elapsed);
    };

    return Object.assign(invoke, { cancel, flush });
  }, [ms]);

  useEffect(() => {
    return () => {
      throttled.cancel();
    };
  }, [throttled]);

  return throttled;
};
